import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import ProductCard from '@/components/ProductCard';
import type { Product } from '@/types/product';

export default function CategoryPage() {
  const { category } = useParams<{ category: string }>();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('featured');
  const [filters, setFilters] = useState({
    brands: [] as string[],
    priceRange: [0, 1000] as [number, number],
    inStock: false,
  });

  useEffect(() => {
    loadProducts();
  }, [category]);

  useEffect(() => {
    applyFilters();
  }, [products, filters, sortBy]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'products'), where('category', '==', category));
      const snapshot = await getDocs(q);
      const prods = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Product));
      setProducts(prods);
    } catch (error) {
      console.error('Error loading products:', error);
    }
    setLoading(false);
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Filter by brand
    if (filters.brands.length > 0) {
      filtered = filtered.filter(p => filters.brands.includes(p.brand));
    }

    // Filter by price
    filtered = filtered.filter(p => {
      const price = p.salePrice || p.price;
      return price >= filters.priceRange[0] && price <= filters.priceRange[1];
    });

    // Filter by stock
    if (filters.inStock) {
      filtered = filtered.filter(p => Object.values(p.stock).some(qty => qty > 0));
    }

    // Sort
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => (a.salePrice || a.price) - (b.salePrice || b.price));
        break;
      case 'price-high':
        filtered.sort((a, b) => (b.salePrice || b.price) - (a.salePrice || a.price));
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        // Featured - keep original order
        break;
    }

    setFilteredProducts(filtered);
  };

  const allBrands = [...new Set(products.map(p => p.brand))].sort();

  return (
    <div className="min-h-screen bg-warm py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-serif font-bold text-slate capitalize mb-2">{category}</h1>
          <p className="text-gray-600">{filteredProducts.length} products</p>
        </div>

        <div className="flex gap-8">
          {/* Sidebar Filters */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-white rounded-lg p-6 sticky top-24">
              <h3 className="font-semibold text-slate mb-4">Filters</h3>

              {/* In Stock */}
              <div className="mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.inStock}
                    onChange={(e) => setFilters({ ...filters, inStock: e.target.checked })}
                    className="w-4 h-4 text-amber focus:ring-amber rounded"
                  />
                  <span className="text-sm text-slate">In Stock Only</span>
                </label>
              </div>

              {/* Brands */}
              <div className="mb-6">
                <h4 className="font-medium text-sm text-slate mb-2">Brand</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {allBrands.map(brand => (
                    <label key={brand} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.brands.includes(brand)}
                        onChange={(e) => {
                          setFilters({
                            ...filters,
                            brands: e.target.checked
                              ? [...filters.brands, brand]
                              : filters.brands.filter(b => b !== brand)
                          });
                        }}
                        className="w-4 h-4 text-amber focus:ring-amber rounded"
                      />
                      <span className="text-sm text-slate">{brand}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <h4 className="font-medium text-sm text-slate mb-2">Price Range</h4>
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    value={filters.priceRange[0]}
                    onChange={(e) => setFilters({
                      ...filters,
                      priceRange: [parseInt(e.target.value) || 0, filters.priceRange[1]]
                    })}
                    className="w-20 px-2 py-1 border rounded text-sm"
                    placeholder="Min"
                  />
                  <span className="text-gray-500">-</span>
                  <input
                    type="number"
                    value={filters.priceRange[1]}
                    onChange={(e) => setFilters({
                      ...filters,
                      priceRange: [filters.priceRange[0], parseInt(e.target.value) || 1000]
                    })}
                    className="w-20 px-2 py-1 border rounded text-sm"
                    placeholder="Max"
                  />
                </div>
              </div>

              <button
                onClick={() => setFilters({ brands: [], priceRange: [0, 1000], inStock: false })}
                className="text-sm text-amber hover:text-amber-dark"
              >
                Clear all filters
              </button>
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Sort */}
            <div className="mb-6 flex justify-end">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber"
              >
                <option value="featured">Featured</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                <option value="name">Name: A-Z</option>
              </select>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block w-12 h-12 border-4 border-forest border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg">
                <p className="text-gray-500">No products found matching your filters.</p>
                <button
                  onClick={() => setFilters({ brands: [], priceRange: [0, 1000], inStock: false })}
                  className="mt-4 text-amber hover:text-amber-dark font-medium"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
